/**
 * geminiAPI.js
 * This module handles all communication with the Google Gemini API.
 * It supports rotating between multiple API keys and implements 
 * exponential backoff for retries.
 */

// Track the current key index globally in the module
let currentKeyIndex = 0;

/**
 * Gets the next available API key from the environment variables.
 */
const getNextApiKey = () => {
    const keys = [
        process.env.REACT_APP_GEMINI_API_KEY1,
        process.env.REACT_APP_GEMINI_API_KEY2,
        process.env.REACT_APP_GEMINI_API_KEY3
    ].filter(key => !!key); // Only use keys that are actually defined

    if (keys.length === 0) {
        throw new Error("No API keys found. Please check your .env file for REACT_APP_GEMINI_API_KEY1, 2, or 3.");
    }

    const key = keys[currentKeyIndex];
    currentKeyIndex = (currentKeyIndex + 1) % keys.length;
    return key;
};

/**
 * Sends a prompt and a base64 encoded image to the Gemini API for analysis.
 * Implements round-robin key rotation and exponential backoff.
 */
const callGeminiAPI = async (prompt, base64ImageData, maxRetries = 5) => {
    let lastError;

    for (let i = 0; i < maxRetries; i++) {
        try {
            // Get a fresh key from the rotation for every attempt
            const apiKey = getNextApiKey();
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;


            const payload = {
                contents: [{
                    role: "user",
                    parts: [
                        { text: prompt },
                        { inlineData: { mimeType: "image/jpeg", data: base64ImageData } }
                    ]
                }],
                generationConfig: {
                    responseMimeType: "application/json",
                    thinkingConfig: { thinkingBudget: 0 }
                }
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            // If rate limited (429) or server error (500+), trigger a retry with backoff
            if (response.status === 429 || response.status >= 500) {
                const delay = Math.pow(2, i) * 1000; // 1s, 2s, 4s, 8s, 16s
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorBody}`);
            }

            const result = await response.json();


            // Extract and clean the JSON response
            if (result.candidates?.[0]?.content?.parts?.[0]?.text) {
                let jsonString = result.candidates[0].content.parts[0].text;

                if (jsonString.includes('```json')) {
                    jsonString = jsonString.split('```json')[1].split('```')[0];
                } else if (jsonString.includes('```')) {
                    jsonString = jsonString.split('```')[1].split('```')[0];
                }

                try {
                    return JSON.parse(jsonString.trim());
                } catch (e) {
                    console.error("Failed to parse JSON response:", jsonString);
                    throw new Error("The model returned an invalid JSON format.");
                }
            } else {
                throw new Error("Could not find valid content in the API response.");
            }

        } catch (err) {
            lastError = err;
            // For network errors, we also retry with backoff
            if (i < maxRetries - 1) {
                const delay = Math.pow(2, i) * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    throw lastError;
};

export default callGeminiAPI;
